import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  X, Save, Clock, DollarSign, FileText, Trophy, Gamepad2, RotateCcw,
  Zap, Calendar, Users, Target, Gift, Key, Package, Sparkles, 
  ChevronDown, ChevronUp, AlertCircle, CheckCircle2, Eye, EyeOff, Lock, Plus, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { GameSearch } from './GameSearch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Rule visibility options
const RULE_VISIBILITY = {
  ALWAYS: 'always',
  LIVE_ONLY: 'live_only',
  BLUR_SCHEDULED: 'blur_scheduled'
};

// Helper to convert ISO to local datetime for datetime-local input
const isoToLocalDatetime = (isoString) => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } catch (e) {
    return '';
  }
};

// Helper to convert local datetime to ISO
const localDatetimeToIso = (localDatetime) => {
  if (!localDatetime) return null;
  try {
    const date = new Date(localDatetime);
    if (isNaN(date.getTime())) return null;
    return date.toISOString();
  } catch (e) {
    return null;
  }
};

// Prize type configuration
const PRIZE_TYPES = [
  { value: 'money', label: 'Cash', icon: DollarSign, color: 'green', description: 'Cash prize via PayPal/Venmo' },
  { value: 'game_code', label: 'Game Code', icon: Key, color: 'blue', description: 'Digital game key (private)' },
  { value: 'item', label: 'Item', icon: Gift, color: 'purple', description: 'Physical/digital prize' }
];

const ChallengeEditModal = ({ 
  challenge, 
  token, 
  onClose, 
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    title: '',
    rules: '',
    rules_config: [],
    bounties: [],
    prize_currency: 'USD',
    max_winners: 1,
    start_time: '',
    end_time: ''
  });
  
  // Prize configs for each place
  const [prizeConfigs, setPrizeConfigs] = useState({
    1: { prize_type: 'money', amount: 100, description: '', code: '', item_details: '' },
    2: { prize_type: 'money', amount: 50, description: '', code: '', item_details: '' },
    3: { prize_type: 'money', amount: 25, description: '', code: '', item_details: '' }
  });
  
  const [selectedGame, setSelectedGame] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState('live'); // 'live' or 'scheduled'
  const [expandedSections, setExpandedSections] = useState({
    prizes: true,
    timing: true,
    game: false,
    rules: true,
    bounties: true,
    advanced: false
  });

  // Initialize form when challenge changes
  useEffect(() => {
    if (challenge) {
      // Parse rules_config from challenge or convert legacy rules string
      let rulesConfig = challenge.rules_config || [];
      if (rulesConfig.length === 0 && challenge.rules) {
        rulesConfig = challenge.rules.split('\n').filter(r => r.trim()).map(text => ({
          text: text.trim(),
          visibility: RULE_VISIBILITY.ALWAYS
        }));
      }
      
      setFormData({
        title: challenge.title || '',
        rules: challenge.rules || '',
        rules_config: rulesConfig,
        bounties: challenge.bounties || [],
        prize_currency: challenge.prize_currency || 'USD',
        max_winners: challenge.max_winners || 1,
        start_time: isoToLocalDatetime(challenge.start_time) || '',
        end_time: isoToLocalDatetime(challenge.end_time) || ''
      });
      
      // Initialize prize configs
      const newConfigs = { ...prizeConfigs };
      if (challenge.prize_1st_config) {
        newConfigs[1] = { ...newConfigs[1], ...challenge.prize_1st_config };
      } else {
        newConfigs[1].amount = challenge.prize_1st || challenge.prize_amount || 100;
      }
      if (challenge.prize_2nd_config) {
        newConfigs[2] = { ...newConfigs[2], ...challenge.prize_2nd_config };
      } else {
        newConfigs[2].amount = challenge.prize_2nd || 50;
      }
      if (challenge.prize_3rd_config) {
        newConfigs[3] = { ...newConfigs[3], ...challenge.prize_3rd_config };
      } else {
        newConfigs[3].amount = challenge.prize_3rd || 25;
      }
      setPrizeConfigs(newConfigs);
      
      if (challenge.game_id) {
        setSelectedGame({
          id: challenge.game_id,
          name: challenge.game_name,
          cover_url: challenge.game_cover_url
        });
      } else {
        setSelectedGame(null);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challenge]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const updatePrizeConfig = (place, field, value) => {
    setPrizeConfigs(prev => ({
      ...prev,
      [place]: { ...prev[place], [field]: value }
    }));
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    setSaving(true);
    try {
      const buildPrizeConfig = (config) => ({
        prize_type: config.prize_type || 'money',
        amount: config.prize_type === 'money' ? (parseFloat(config.amount) || 0) : null,
        description: config.description || null,
        code: config.prize_type === 'game_code' ? config.code : null,
        item_details: config.prize_type === 'item' ? config.item_details : null
      });

      // Convert rules_config to rules string for backwards compatibility
      const rulesString = formData.rules_config.map(r => r.text).join('\n');

      await axios.put(`${API}/admin/challenges/${challenge.id}`, {
        title: formData.title,
        rules: rulesString,
        rules_config: formData.rules_config,
        prize_amount: prizeConfigs[1].amount || 100,
        prize_1st: prizeConfigs[1].amount,
        prize_2nd: formData.max_winners >= 2 ? prizeConfigs[2].amount : null,
        prize_3rd: formData.max_winners >= 3 ? prizeConfigs[3].amount : null,
        prize_currency: formData.prize_currency,
        prize_1st_config: buildPrizeConfig(prizeConfigs[1]),
        prize_2nd_config: formData.max_winners >= 2 ? buildPrizeConfig(prizeConfigs[2]) : null,
        prize_3rd_config: formData.max_winners >= 3 ? buildPrizeConfig(prizeConfigs[3]) : null,
        bounties: formData.bounties || [],
        max_winners: formData.max_winners,
        start_time: formData.start_time ? localDatetimeToIso(formData.start_time) : null,
        end_time: formData.end_time ? localDatetimeToIso(formData.end_time) : null,
        game_id: selectedGame?.id || null,
        game_name: selectedGame?.name || null,
        game_cover_url: selectedGame?.cover_url || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Challenge updated successfully!');
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Failed to update challenge:', error);
      toast.error(error.response?.data?.detail || 'Failed to update challenge');
    } finally {
      setSaving(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      active: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/50', icon: Zap, label: 'LIVE' },
      scheduled: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/50', icon: Calendar, label: 'SCHEDULED' },
      ended: { bg: 'bg-gray-500/20', text: 'text-gray-400', border: 'border-gray-500/50', icon: CheckCircle2, label: 'ENDED' }
    };
    return configs[status] || configs.scheduled;
  };

  const calculateTotalPrize = () => {
    let total = 0;
    if (prizeConfigs[1].prize_type === 'money') total += parseFloat(prizeConfigs[1].amount) || 0;
    if (formData.max_winners >= 2 && prizeConfigs[2].prize_type === 'money') total += parseFloat(prizeConfigs[2].amount) || 0;
    if (formData.max_winners >= 3 && prizeConfigs[3].prize_type === 'money') total += parseFloat(prizeConfigs[3].amount) || 0;
    return total;
  };

  if (!challenge) return null;
  
  const statusConfig = getStatusConfig(challenge.status);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop with blur */}
      <div 
        className="absolute inset-0 bg-black/90 backdrop-blur-md"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-2xl border border-red-500/30 shadow-2xl shadow-red-500/10 mx-2 sm:mx-0">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-900 to-red-950/30" />
        
        {/* Header */}
        <div className="relative sticky top-0 z-10 bg-gradient-to-r from-red-900/40 to-transparent border-b border-red-500/20 px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <div className="p-1.5 sm:p-2 bg-yellow-500/20 rounded-lg border border-yellow-500/30 flex-shrink-0">
                <Trophy className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400" />
              </div>
              <div>
                <h2 className="text-base sm:text-xl font-black text-white tracking-wide" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  EDIT CHALLENGE
                </h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${statusConfig.bg} ${statusConfig.text} border ${statusConfig.border}`}>
                    <StatusIcon className="w-3 h-3" />
                    {statusConfig.label}
                  </span>
                  <span className="text-xs text-gray-500">ID: {challenge.id?.slice(0, 8)}...</span>
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-10 w-10 p-0 rounded-full hover:bg-red-500/20 hover:text-red-400 transition-colors"
              onClick={onClose}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="relative overflow-y-auto max-h-[calc(90vh-180px)] px-4 sm:px-6 py-4 space-y-4 custom-scrollbar">
          
          {/* Title Section */}
          <div className="space-y-2">
            <Label className="text-sm font-bold text-gray-300 flex items-center gap-2">
              <FileText className="w-4 h-4 text-red-400" />
              Challenge Title
            </Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="bg-black/50 border-red-500/30 focus:border-red-500 h-12 text-lg font-semibold placeholder:text-gray-600"
              placeholder="Enter an epic challenge name..."
              data-testid="edit-challenge-title"
            />
          </div>

          {/* Prize Configuration Section */}
          <div className="rounded-xl border border-green-500/30 overflow-hidden">
            <button
              onClick={() => toggleSection('prizes')}
              className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-green-500/10 to-transparent hover:from-green-500/20 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <Gift className="w-5 h-5 text-green-400" />
                </div>
                <div className="text-left">
                  <h3 className="font-bold text-green-400">Prize Configuration</h3>
                  <p className="text-xs text-gray-500">
                    Total: ${calculateTotalPrize()} {formData.prize_currency} • {formData.max_winners} winner{formData.max_winners > 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              {expandedSections.prizes ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </button>
            
            {expandedSections.prizes && (
              <div className="p-4 space-y-4 bg-black/30">
                {/* Number of Winners */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                  <div className="flex items-center gap-3">
                    <Label className="text-sm text-gray-400 flex items-center gap-2 whitespace-nowrap">
                      <Users className="w-4 h-4" /> Winners:
                    </Label>
                    <div className="flex gap-2">
                      {[1, 2, 3].map(num => (
                        <button
                          key={num}
                          onClick={() => setFormData({ ...formData, max_winners: num })}
                          className={`w-12 h-10 rounded-lg font-bold transition-all ${
                            formData.max_winners === num
                              ? 'bg-green-500 text-black shadow-lg shadow-green-500/30'
                              : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                          }`}
                        >
                          {num}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:ml-auto">
                    <Label className="text-xs text-gray-500">Currency:</Label>
                    <Input
                      value={formData.prize_currency}
                      onChange={(e) => setFormData({ ...formData, prize_currency: e.target.value.toUpperCase() })}
                      className="w-20 h-8 bg-black/50 border-gray-700 text-center text-sm"
                      placeholder="USD"
                    />
                  </div>
                </div>

                {/* Prize Cards for each place */}
                {[1, 2, 3].map(place => (
                  formData.max_winners >= place && (
                    <PrizeCard
                      key={place}
                      place={place}
                      config={prizeConfigs[place]}
                      currency={formData.prize_currency}
                      onUpdate={(field, value) => updatePrizeConfig(place, field, value)}
                    />
                  )
                ))}
              </div>
            )}
          </div>

          {/* Rules Section with Preview Toggle */}
          <div className="rounded-xl border border-blue-500/30 overflow-hidden">
            <button
              onClick={() => toggleSection('rules')}
              className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-blue-500/10 to-transparent hover:from-blue-500/20 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Target className="w-5 h-5 text-blue-400" />
                </div>
                <div className="text-left">
                  <h3 className="font-bold text-blue-400">Challenge Rules</h3>
                  <p className="text-xs text-gray-500">
                    {formData.rules_config.length} rule{formData.rules_config.length !== 1 ? 's' : ''} configured
                  </p>
                </div>
              </div>
              {expandedSections.rules ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </button>
            
            {expandedSections.rules && (
              <div className="p-4 space-y-4 bg-black/30">
                {/* Preview Toggle */}
                <div className="flex items-center justify-between p-2 bg-gray-800/50 rounded-lg">
                  <span className="text-xs text-gray-400 flex items-center gap-2">
                    <Eye className="w-4 h-4" /> Preview Mode:
                  </span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setPreviewMode('live')}
                      className={`px-3 py-1 text-xs rounded-lg transition-all ${
                        previewMode === 'live'
                          ? 'bg-green-500 text-black font-bold'
                          : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                      }`}
                    >
                      🟢 Live
                    </button>
                    <button
                      onClick={() => setPreviewMode('scheduled')}
                      className={`px-3 py-1 text-xs rounded-lg transition-all ${
                        previewMode === 'scheduled'
                          ? 'bg-yellow-500 text-black font-bold'
                          : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                      }`}
                    >
                      ⏰ Scheduled
                    </button>
                  </div>
                </div>
                
                {/* Rules Preview */}
                <div className="p-3 bg-gray-900/50 rounded-lg border border-gray-700">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">
                    Preview: How rules appear when {previewMode === 'live' ? 'LIVE' : 'SCHEDULED'}
                  </p>
                  <ul className="space-y-1">
                    {formData.rules_config.map((rule, i) => {
                      // Apply visibility rules
                      if (previewMode === 'scheduled') {
                        if (rule.visibility === 'live_only') return null;
                        if (rule.visibility === 'blur_scheduled') {
                          return (
                            <li key={i} className="text-xs text-gray-400 flex items-start gap-2 blur-sm">
                              <span className="text-yellow-400">•</span>
                              <span>{rule.text}</span>
                            </li>
                          );
                        }
                      }
                      return (
                        <li key={i} className="text-xs text-gray-300 flex items-start gap-2">
                          <span className="text-green-400">•</span>
                          <span>{rule.text}</span>
                        </li>
                      );
                    })}
                  </ul>
                  {formData.rules_config.length === 0 && (
                    <p className="text-xs text-gray-600 italic">No rules added yet</p>
                  )}
                </div>
                
                {/* Rules Editor */}
                <RulesEditorInline
                  rulesConfig={formData.rules_config}
                  onChange={(newConfig) => setFormData({ ...formData, rules_config: newConfig })}
                />
              </div>
            )}
          </div>

          {/* Bounties Section */}
          <div className="rounded-xl border border-orange-500/30 overflow-hidden">
            <button
              onClick={() => toggleSection('bounties')}
              className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-orange-500/10 to-transparent hover:from-orange-500/20 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-500/20 rounded-lg">
                  <Gift className="w-5 h-5 text-orange-400" />
                </div>
                <div className="text-left">
                  <h3 className="font-bold text-orange-400">Bounties (Optional)</h3>
                  <p className="text-xs text-gray-500">
                    {formData.bounties.length} bounty{formData.bounties.length !== 1 ? 'ies' : ''} added
                  </p>
                </div>
              </div>
              {expandedSections.bounties ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </button>
            
            {expandedSections.bounties && (
              <div className="p-4 space-y-4 bg-black/30">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-500">
                    Add optional extra prizes for completing additional rules
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setFormData({ 
                      ...formData, 
                      bounties: [...formData.bounties, { prize: '', rule: '' }] 
                    })}
                    className="h-8 px-3 text-xs border-orange-500/30 text-orange-400 hover:bg-orange-500/20"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Bounty
                  </Button>
                </div>

                {formData.bounties.length > 0 ? (
                  <div className="space-y-3">
                    {formData.bounties.map((bounty, idx) => (
                      <div key={idx} className="p-3 bg-black/40 border border-orange-900/30 rounded-lg space-y-2">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-orange-400 font-bold">Bounty #{idx + 1}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const newBounties = formData.bounties.filter((_, i) => i !== idx);
                              setFormData({ ...formData, bounties: newBounties });
                            }}
                            className="h-6 w-6 p-0 text-red-400 hover:bg-red-500/20"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                        <div className="space-y-2">
                          <div>
                            <Label className="text-xs text-yellow-300 mb-1">Prize</Label>
                            <Input
                              placeholder="e.g., Extra $50, Exclusive emote"
                              value={bounty.prize}
                              onChange={(e) => {
                                const newBounties = [...formData.bounties];
                                newBounties[idx] = { ...newBounties[idx], prize: e.target.value };
                                setFormData({ ...formData, bounties: newBounties });
                              }}
                              className="bg-black/50 border-yellow-900/30 text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-orange-300 mb-1">Rule/Condition</Label>
                            <Input
                              placeholder="e.g., Complete under 1 hour, No damage taken"
                              value={bounty.rule}
                              onChange={(e) => {
                                const newBounties = [...formData.bounties];
                                newBounties[idx] = { ...newBounties[idx], rule: e.target.value };
                                setFormData({ ...formData, bounties: newBounties });
                              }}
                              className="bg-black/50 border-orange-900/30 text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-600 italic text-center py-4">
                    No bounties added. Click "Add Bounty" to create optional extra prizes.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Timing Section */}
          <div className="rounded-xl border border-yellow-500/30 overflow-hidden">
            <button
              onClick={() => toggleSection('timing')}
              className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-yellow-500/10 to-transparent hover:from-yellow-500/20 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-500/20 rounded-lg">
                  <Clock className="w-5 h-5 text-yellow-400" />
                </div>
                <div className="text-left">
                  <h3 className="font-bold text-yellow-400">Schedule & Timing</h3>
                  <p className="text-xs text-gray-500">
                    {formData.start_time ? `Starts: ${new Date(formData.start_time).toLocaleString()}` : 'No start time set'}
                  </p>
                </div>
              </div>
              {expandedSections.timing ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </button>
            
            {expandedSections.timing && (
              <div className="p-4 space-y-4 bg-black/30">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Start Time */}
                  <div className="space-y-2">
                    <Label className="text-xs text-green-400 flex items-center gap-1">
                      <Zap className="w-3 h-3" /> Start Time
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        type="datetime-local"
                        value={formData.start_time}
                        onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                        className="bg-black/50 border-green-500/30 focus:border-green-500 text-sm flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-9 px-2 border-gray-700 hover:bg-gray-800"
                        onClick={() => setFormData({ ...formData, start_time: '' })}
                      >
                        <RotateCcw className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* End Time */}
                  <div className="space-y-2">
                    <Label className="text-xs text-red-400 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> End Time
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        type="datetime-local"
                        value={formData.end_time}
                        onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                        className="bg-black/50 border-red-500/30 focus:border-red-500 text-sm flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-9 px-2 border-gray-700 hover:bg-gray-800"
                        onClick={() => setFormData({ ...formData, end_time: '' })}
                      >
                        <RotateCcw className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Quick Time Actions */}
                <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-800">
                  <span className="text-xs text-gray-500 mr-2">Quick set:</span>
                  <Button
                    type="button"
                    size="sm"
                    className="h-7 px-3 text-xs bg-green-600 hover:bg-green-700"
                    onClick={() => {
                      const now = new Date();
                      setFormData({ ...formData, start_time: now.toISOString().slice(0, 16) });
                    }}
                  >
                    Start Now
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 px-3 text-xs border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                    onClick={() => {
                      const future = new Date(Date.now() + 24 * 60 * 60 * 1000);
                      setFormData({ ...formData, end_time: future.toISOString().slice(0, 16) });
                    }}
                  >
                    +24h End
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 px-3 text-xs border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                    onClick={() => {
                      const future = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
                      setFormData({ ...formData, end_time: future.toISOString().slice(0, 16) });
                    }}
                  >
                    +7d End
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 px-3 text-xs border-gray-600 text-gray-400 hover:bg-gray-800"
                    onClick={() => setFormData({ ...formData, start_time: '', end_time: '' })}
                  >
                    Clear All
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Game Section - Higher z-index for dropdown */}
          <div className="rounded-xl border border-purple-500/30 overflow-visible relative z-20">
            <button
              onClick={() => toggleSection('game')}
              className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-purple-500/10 to-transparent hover:from-purple-500/20 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Gamepad2 className="w-5 h-5 text-purple-400" />
                </div>
                <div className="text-left">
                  <h3 className="font-bold text-purple-400">Game Category</h3>
                  <p className="text-xs text-gray-500">
                    {selectedGame ? selectedGame.name : 'No game selected (optional)'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {selectedGame?.cover_url && (
                  <img src={selectedGame.cover_url} alt="" className="w-8 h-10 object-cover rounded" />
                )}
                {expandedSections.game ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
              </div>
            </button>
            
            {expandedSections.game && (
              <div className="p-4 bg-black/30 overflow-visible relative z-30">
                <GameSearch 
                  selectedGame={selectedGame} 
                  onSelectGame={setSelectedGame}
                  compact
                />
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="relative sticky bottom-0 bg-gradient-to-t from-gray-900 via-gray-900 to-transparent border-t border-red-500/20 px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex gap-2 sm:gap-3">
            <Button
              variant="outline"
              className="flex-1 h-12 border-gray-700 hover:bg-gray-800 hover:border-gray-600 font-semibold"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              className="flex-1 h-12 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 font-bold shadow-lg shadow-green-500/20"
              onClick={handleSave}
              disabled={saving}
              data-testid="save-challenge-btn"
            >
              {saving ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
      
      {/* Custom scrollbar styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.3);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(239, 68, 68, 0.3);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(239, 68, 68, 0.5);
        }
      `}</style>
    </div>
  );
};

// Prize Card Component for each place
const PrizeCard = ({ place, config, currency, onUpdate }) => {
  const placeConfig = {
    1: { label: '1st Place', emoji: '🥇', color: 'yellow', bgGradient: 'from-yellow-500/20 to-yellow-600/10' },
    2: { label: '2nd Place', emoji: '🥈', color: 'gray', bgGradient: 'from-gray-400/20 to-gray-500/10' },
    3: { label: '3rd Place', emoji: '🥉', color: 'amber', bgGradient: 'from-amber-500/20 to-amber-600/10' }
  };
  
  const { label, emoji, color, bgGradient } = placeConfig[place];
  const colorClasses = {
    yellow: { border: 'border-yellow-500/30', text: 'text-yellow-400', bg: 'bg-yellow-500' },
    gray: { border: 'border-gray-400/30', text: 'text-gray-300', bg: 'bg-gray-400' },
    amber: { border: 'border-amber-500/30', text: 'text-amber-400', bg: 'bg-amber-500' }
  };
  const colors = colorClasses[color];

  return (
    <div className={`rounded-lg border ${colors.border} bg-gradient-to-r ${bgGradient} p-3 sm:p-4`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
        <span className={`font-bold ${colors.text} flex items-center gap-2`}>
          <span className="text-xl">{emoji}</span>
          {label}
        </span>
        
        {/* Prize Type Selector */}
        <div className="flex flex-wrap gap-1">
          {PRIZE_TYPES.map(({ value, label, icon: Icon, color: typeColor }) => (
            <button
              key={value}
              onClick={() => onUpdate('prize_type', value)}
              className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${
                config.prize_type === value
                  ? typeColor === 'green' ? 'bg-green-500 text-black shadow-lg shadow-green-500/30'
                    : typeColor === 'blue' ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                    : 'bg-purple-500 text-white shadow-lg shadow-purple-500/30'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              <Icon className="w-3 h-3" />
              {label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Prize Type Specific Fields */}
      {config.prize_type === 'money' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-gray-400 mb-1 block">Amount ({currency})</Label>
            <Input
              type="number"
              value={config.amount || ''}
              onChange={(e) => onUpdate('amount', parseFloat(e.target.value) || 0)}
              className="bg-black/50 border-green-500/30 h-10"
              placeholder="100"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-400 mb-1 block">Description (optional)</Label>
            <Input
              value={config.description || ''}
              onChange={(e) => onUpdate('description', e.target.value)}
              className="bg-black/50 border-gray-700 h-10"
              placeholder="Paid via PayPal"
            />
          </div>
        </div>
      )}
      
      {config.prize_type === 'game_code' && (
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-gray-400 mb-1 block">Game/Product (shown publicly)</Label>
            <Input
              value={config.description || ''}
              onChange={(e) => onUpdate('description', e.target.value)}
              className="bg-black/50 border-blue-500/30 h-10"
              placeholder="e.g., Elden Ring Steam Key"
            />
          </div>
          <div>
            <Label className="text-xs text-blue-400 mb-1 flex items-center gap-1">
              <Key className="w-3 h-3" /> Game Code (private - only winner sees)
            </Label>
            <Input
              value={config.code || ''}
              onChange={(e) => onUpdate('code', e.target.value)}
              className="bg-black/50 border-blue-500/30 h-10 font-mono text-blue-400"
              placeholder="XXXX-XXXX-XXXX-XXXX"
            />
          </div>
        </div>
      )}
      
      {config.prize_type === 'item' && (
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-gray-400 mb-1 block">Item Name (shown publicly)</Label>
            <Input
              value={config.description || ''}
              onChange={(e) => onUpdate('description', e.target.value)}
              className="bg-black/50 border-purple-500/30 h-10"
              placeholder="e.g., Gaming Headset, T-Shirt"
            />
          </div>
          <div>
            <Label className="text-xs text-purple-400 mb-1 flex items-center gap-1">
              <Package className="w-3 h-3" /> Item Details (private - for shipping reference)
            </Label>
            <Input
              value={config.item_details || ''}
              onChange={(e) => onUpdate('item_details', e.target.value)}
              className="bg-black/50 border-purple-500/30 h-10"
              placeholder="Brand, model, size, color..."
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Inline Rules Editor Component for Edit Modal
const RulesEditorInline = ({ rulesConfig, onChange }) => {
  const [newRuleText, setNewRuleText] = useState('');

  const addRule = () => {
    if (!newRuleText.trim()) return;
    onChange([...rulesConfig, { text: newRuleText.trim(), visibility: RULE_VISIBILITY.ALWAYS }]);
    setNewRuleText('');
  };

  const updateRule = (index, field, value) => {
    const updated = rulesConfig.map((rule, i) => 
      i === index ? { ...rule, [field]: value } : rule
    );
    onChange(updated);
  };

  const removeRule = (index) => {
    onChange(rulesConfig.filter((_, i) => i !== index));
  };

  const moveRule = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= rulesConfig.length) return;
    const updated = [...rulesConfig];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-gray-400 font-bold">Edit Rules</Label>
        <div className="flex items-center gap-2 text-[10px] text-gray-500">
          <span className="flex items-center gap-1"><Eye className="w-3 h-3 text-green-400" /> Always</span>
          <span className="flex items-center gap-1"><EyeOff className="w-3 h-3 text-yellow-400" /> Live only</span>
          <span className="flex items-center gap-1"><Lock className="w-3 h-3 text-blue-400" /> Blur</span>
        </div>
      </div>

      {/* Rules List */}
      <div className="space-y-2 max-h-[200px] overflow-y-auto">
        {rulesConfig.map((rule, index) => (
          <div key={index} className="flex items-start gap-2 p-2 bg-black/30 border border-gray-800 rounded-lg group">
            {/* Number and controls */}
            <div className="flex flex-col items-center gap-0.5 pt-1">
              <span className="text-[10px] text-gray-500 font-bold">{index + 1}</span>
              <button 
                onClick={() => moveRule(index, -1)}
                disabled={index === 0}
                className="text-gray-600 hover:text-gray-400 disabled:opacity-30"
              >
                <ChevronUp className="w-3 h-3" />
              </button>
              <button 
                onClick={() => moveRule(index, 1)}
                disabled={index === rulesConfig.length - 1}
                className="text-gray-600 hover:text-gray-400 disabled:opacity-30"
              >
                <ChevronDown className="w-3 h-3" />
              </button>
            </div>

            {/* Rule content */}
            <div className="flex-1 space-y-1">
              <Input
                value={rule.text}
                onChange={(e) => updateRule(index, 'text', e.target.value)}
                className="bg-black/50 border-gray-700 h-7 text-xs"
                placeholder="Enter rule text..."
              />
              
              {/* Visibility selector */}
              <div className="flex items-center gap-1">
                {[
                  { value: RULE_VISIBILITY.ALWAYS, label: 'Always', icon: Eye, color: 'green' },
                  { value: RULE_VISIBILITY.LIVE_ONLY, label: 'Live', icon: EyeOff, color: 'yellow' },
                  { value: RULE_VISIBILITY.BLUR_SCHEDULED, label: 'Blur', icon: Lock, color: 'blue' }
                ].map(({ value, label, icon: Icon, color }) => (
                  <button
                    key={value}
                    onClick={() => updateRule(index, 'visibility', value)}
                    className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium transition-all ${
                      rule.visibility === value 
                        ? `bg-${color}-500 text-black` 
                        : 'bg-gray-800 text-gray-500 hover:bg-gray-700'
                    }`}
                  >
                    <Icon className="w-2.5 h-2.5" />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Delete */}
            <button
              onClick={() => removeRule(index)}
              className="h-6 w-6 flex items-center justify-center text-gray-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>

      {/* Add new rule */}
      <div className="flex gap-2 pt-2 border-t border-gray-800">
        <Input
          value={newRuleText}
          onChange={(e) => setNewRuleText(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && addRule()}
          className="bg-black/50 border-gray-700 h-8 text-sm flex-1"
          placeholder="Type a new rule and press Enter..."
        />
        <Button
          type="button"
          size="sm"
          onClick={addRule}
          disabled={!newRuleText.trim()}
          className="bg-blue-600 hover:bg-blue-700 h-8"
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default ChallengeEditModal;
